import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Input, Label, Row, Col } from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import { RejaPendientes, armaBloque, cambiaCeldaBloques, llenaBloques, lineasCapturadas } from "./RejaPendientes";
import { hoyLocal } from "./fechaLocal";

// Devuelve desde la propia nota: trae lo que se surtio por color y talla (menos
// lo que ya habia regresado) y se captura lo que devuelve el cliente. Si la
// mercancia viene en buen estado regresa al inventario; si viene con defecto se
// registra pero no se suma a lo vendible.
const headers = () => ({ Authorization: `Bearer: ${localStorage.getItem("app_token")}` });
const num = (n) => new Intl.NumberFormat("en-US").format(Number(n) || 0);


function DevolverNotaModal({ abierto, cerrar, nota, user, onDevuelto }) {
  const [bloques, setBloques] = useState([]);
  const [cab, setCab] = useState(null);
  const [fecha, setFecha] = useState(hoyLocal());
  const [estado, setEstado] = useState("Buenas");
  const [observaciones, setObservaciones] = useState("NA");
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!abierto || !nota) return;
    setBloques([]); setCab(null); setFecha(hoyLocal()); setEstado("Buenas"); setObservaciones("NA"); setGuardando(false);
    axios.get(`${process.env.REACT_APP_URL_DEVOLUCIONES_PENDIENTES}/${nota._id}`, { headers: headers() })
      .then((r) => {
        setCab(r.data.nota);
        const lineas = (r.data.lineas || [])
          .filter((l) => l.pendiente > 0)
          .map((l) => ({
            _id: l.idArtSurtido,
            articulo: l.articulo,
            color: l.color,
            talla: l.talla,
            pendiente: l.pendiente,
            precio: l.precio,
          }));
        setBloques(lineas.length
          ? [armaBloque(String(nota._id), `Nota ${r.data.nota.idSurtido}`, r.data.nota.cliente || "", lineas)]
          : []);
      })
      .catch((e) => Swal.fire({ icon: "error", title: "No se pudo traer la nota", text: e.response?.data?.message || e.message }));
  }, [abierto, nota]); // eslint-disable-line

  const capturadas = lineasCapturadas(bloques);
  const piezas = capturadas.reduce((s, c) => s + c.cantidad, 0);
  const importe = capturadas.reduce((s, c) => s + c.cantidad * (c.linea.precio || 0), 0);

  async function devolver() {
    if (!capturadas.length) { Swal.fire({ icon: "warning", title: "Sin piezas", text: "Captura lo que regresa el cliente." }); return; }
    const ok = await Swal.fire({
      icon: "question", title: `Devolver ${num(piezas)} piezas`,
      html: `${capturadas.length} renglón(es) · $${num(importe)}<br>` +
            (estado === "Buenas" ? "La mercancía <b>regresa al inventario</b>." : "Se registra <b>con defecto</b> y no entra al inventario."),
      showCancelButton: true, confirmButtonText: "Sí, devolver", cancelButtonText: "Cancelar",
    });
    if (!ok.isConfirmed) return;
    setGuardando(true);
    try {
      const r = await axios.post(process.env.REACT_APP_URL_DEVOLUCIONES_DESDE_NOTA, {
        surtidos: nota._id, clientes: cab?.clientes, areas: cab?.areas, fecha,
        observaciones, impuestos: cab?.impuestos || 0, estado,
        colaboradores: cab?.colaboradores, user,
        lineas: capturadas.map((c) => ({
          articulos: c.linea.articulo?._id,
          colores: c.linea.color?._id || null,
          tallas: c.linea.talla?._id || null,
          cantidad: c.cantidad,
          precio: c.linea.precio,
        })),
      }, { headers: headers() });
      setGuardando(false);
      await Swal.fire({
        icon: "success", title: `Devolución ${r.data.idDevolucion}`,
        html: `${num(r.data.total_cantidad)} piezas · $${num(r.data.total_general)}<br>` +
              (r.data.regresoAlInventario ? "La mercancía regresó al inventario." : "Quedó registrada como defectuosa."),
      });
      cerrar();
      if (onDevuelto) onDevuelto();
    } catch (error) {
      setGuardando(false);
      Swal.fire({ icon: "error", title: "No se pudo devolver", text: error.response?.data?.message || error.message });
    }
  }

  return (
    <Modal size="xl" isOpen={abierto} toggle={cerrar}>
      <ModalHeader toggle={cerrar}>
        Devolución de la nota {cab?.idSurtido || nota?.numero} {cab?.cliente ? `· ${cab.cliente}` : ""}
      </ModalHeader>
      <ModalBody>
        <Row className="mb-3">
          <Col md={2}>
            <Label>Fecha</Label>
            <Input bsSize="sm" type="date" value={fecha} onChange={(e) => { const v = e.target.value; setFecha(v); }} />
          </Col>
          <Col md={3}>
            <Label>¿Cómo regresa?</Label>
            <Input bsSize="sm" type="select" value={estado} onChange={(e) => { const v = e.target.value; setEstado(v); }}>
              <option value="Buenas">En buen estado (vuelve al inventario)</option>
              <option value="Defecto">Con defecto (no entra al inventario)</option>
            </Input>
          </Col>
          <Col md={3}>
            <Label>Almacén</Label>
            <Input bsSize="sm" value={cab?.almacen || ""} disabled />
          </Col>
          <Col md={4}>
            <Label>Observaciones</Label>
            <Input bsSize="sm" value={observaciones} onChange={(e) => { const v = e.target.value; setObservaciones(v); }} />
          </Col>
        </Row>
        <RejaPendientes
          bloques={bloques}
          etiquetaPendiente="Se surtió"
          onCambia={(b, a, c, t, v) => setBloques((prev) => cambiaCeldaBloques(prev, b, a, c, t, v))}
          onLlenar={(b, a, c) => setBloques((prev) => llenaBloques(prev, b, a, true, c))}
          onLimpiar={(b, a, c) => setBloques((prev) => llenaBloques(prev, b, a, false, c))}
          onQuitar={() => {}}
        />
      </ModalBody>
      <ModalFooter>
        <Label style={{ marginRight: "auto", color: "#64748b", fontSize: 12 }}>
          {capturadas.length} renglón(es) · <b>{num(piezas)}</b> piezas · ${num(importe)}
        </Label>
        <Button color="success" onClick={devolver} disabled={guardando || !piezas}>
          {guardando ? "Guardando..." : "Devolver"}
        </Button>
        <Button color="secondary" onClick={cerrar}>Cerrar</Button>
      </ModalFooter>
    </Modal>
  );
}

export default DevolverNotaModal;
